"use strict";
/**
 * The Kalshi series-ticker presets shown on step 2 of the additional-markets
 * modal, resolved per league from the primary market's series.
 *
 * Presets are returned grouped by league so the UI can show the shared league
 * prefix once as a header (`🏀 WNBA · KXWNBA`) and put only the market-type
 * suffix on each button (`GAME`, `1QSPREAD`, `HIT`) — the full ticker still
 * drives the search, it just isn't repeated on every button.
 *
 * A preset's suffix comes from two sources, combined per league:
 *
 *   1. **Game/period markets** — reused verbatim from the events-page selector
 *      (`getKalshiSuffixOptions`), so a league automatically gets exactly the
 *      periods it supports (MLB's F5, soccer's halves-no-quarters, …) and the
 *      two selectors can never drift.
 *   2. **Per-sport extras** — the series unique to a sport that the game/period
 *      system deliberately omits: player props (`HIT`, `KS`, …) and a team
 *      total. Curated here per league; only MLB is populated so far. Adding a
 *      sport is one `EXTRA_SUFFIXES_BY_PREFIX` entry of verified suffix tokens
 *      — no code change.
 *
 * With no resolved league (unknown/custom league, or a primary Kalshi doesn't
 * cover) the caller passes `undefined`/`null` and gets one group per known
 * league, game/period only (no props) — always valid, clickable tickers
 * grouped under their own headers rather than a single-league guess.
 *
 * Pure and DOM/state-free; the modal wires it to app state.
 *
 * @module markets/kalshi-series-presets
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.getKalshiSeriesPresetGroups = getKalshiSeriesPresetGroups;
const const_1 = require("../const");
/**
 * Series unique to a sport, beyond the shared game/period set — player props
 * plus the team-total market. Keyed by league prefix; a league absent here
 * shows game/period markets only. Values are verified Kalshi suffix tokens
 * (a wrong token renders a preset that searches a non-existent series).
 *
 * MLB props: HIT (hits), KS (strikeouts), HR (home runs), HRR (hits+runs+RBIs),
 * TB (total bases), RFI (run first inning). TEAMTOTAL is a team-total market.
 * These preserve the historic MLB-only preset list.
 */
const EXTRA_SUFFIXES_BY_PREFIX = new Map([
    [
        (0, const_1.toLeaguePrefix)(const_1.LEAGUES.MLB),
        ["TEAMTOTAL", "HIT", "KS", "HR", "HRR", "TB", "RFI"],
    ],
]);
/** Friendly league label for a prefix, falling back to the prefix itself. */
const LABEL_BY_PREFIX = new Map(const_1.KALSHI_LEAGUE_PREFIXES.map((p) => [p.value, p.label]));
/** Suffix tokens for a league prefix: game/period, optionally plus extras. */
function suffixesForPrefix(prefix, includeExtras) {
    const gamePeriod = (0, const_1.getKalshiSuffixOptions)(prefix).map((o) => o.value);
    const extras = includeExtras
        ? (EXTRA_SUFFIXES_BY_PREFIX.get(prefix) ?? [])
        : [];
    return [...new Set([...gamePeriod, ...extras])];
}
/** Build one league group from its prefix. */
function groupForPrefix(prefix, includeExtras) {
    return {
        prefix,
        label: LABEL_BY_PREFIX.get(prefix) ?? prefix,
        presets: suffixesForPrefix(prefix, includeExtras).map((suffix) => ({
            ticker: `${prefix}${suffix}`,
            suffix,
        })),
    };
}
/**
 * Step-2 Kalshi preset groups for a league prefix (`"KXMLB"`), or the
 * sport-agnostic fallback (one group per league, game/period only) when the
 * league is unresolved (`undefined`/`null`).
 */
function getKalshiSeriesPresetGroups(leaguePrefix) {
    if (!leaguePrefix) {
        return const_1.KALSHI_LEAGUE_PREFIXES.map((p) => groupForPrefix(p.value, false));
    }
    return [groupForPrefix(leaguePrefix, true)];
}
